/**
 * CartPage.duck.js
 * Server-side saved listings via Sharetribe currentUser.privateData.savedListings
 * Falls back to localStorage (cart.js) for unauthenticated users.
 */
import { setCurrentUser } from '../../ducks/user.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { createSlug } from '../../util/urlHelpers';

// ── Toggle (add/remove) a listing ID in privateData.savedListings ─────────────

export const toggleSavedListing = listingId => (dispatch, getState, sdk) => {
  const currentUser = getState().user.currentUser;
  if (!currentUser) return Promise.resolve(null);

  const existing =
    currentUser.attributes?.profile?.privateData?.savedListings || [];
  const alreadySaved = existing.includes(listingId);
  const updated = alreadySaved
    ? existing.filter(id => id !== listingId)
    : [...existing, listingId];

  return sdk.currentUser
    .updateProfile({ privateData: { savedListings: updated } }, { expand: true })
    .then(response => {
      const entities = denormalisedResponseEntities(response);
      if (entities.length === 1) dispatch(setCurrentUser(entities[0]));
      return !alreadySaved; // true = now saved, false = now removed
    })
    .catch(e => {
      console.error('toggleSavedListing failed', storableError(e));
      return null;
    });
};

// ── Fetch full listing data for a list of IDs ─────────────────────────────────

export const fetchSavedListingData = ids => (dispatch, getState, sdk) => {
  if (!ids || ids.length === 0) return Promise.resolve([]);

  return sdk.listings
    .query({
      ids,
      include: ['images'],
      'fields.listing': [
        'title',
        'price',
        'state',
        'publicData.designer',
        'publicData.color',
        'publicData.collection',
      ],
      'fields.image': [
        'variants.listing-card',
        'variants.listing-card-2x',
        'variants.scaled-medium',
      ],
    })
    .then(response => {
      // Attach included images to their listings
      const listings = response.data.data || [];
      const included = response.data.included || [];
      const imageMap = {};
      included
        .filter(e => e.type === 'image')
        .forEach(img => { imageMap[img.id.uuid] = img; });

      return listings
        .filter(l => l.attributes?.state === 'published')
        .map(l => {
          const imageRels = l.relationships?.images?.data || [];
          const firstImageId = imageRels[0]?.id?.uuid;
          const firstImage = firstImageId ? imageMap[firstImageId] : null;
          const imgUrl =
            firstImage?.attributes?.variants?.['listing-card-2x']?.url ||
            firstImage?.attributes?.variants?.['listing-card']?.url ||
            firstImage?.attributes?.variants?.['scaled-medium']?.url ||
            null;

          const pub = l.attributes?.publicData || {};
          const price = l.attributes?.price;
          return {
            listingId: l.id.uuid,
            slug: createSlug(l.attributes?.title || ''),
            title: l.attributes?.title || '',
            designer: pub.designer || '',
            priceAmount: price?.amount || null,
            currency: price?.currency || 'USD',
            imageUrl: imgUrl,
          };
        });
    })
    .catch(e => {
      console.error('fetchSavedListingData failed', storableError(e));
      return [];
    });
};
